import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";
import { getRoleNameById } from "./manage-coaches/utils/manage-coaches-utils";

interface PreferencesAccessLocals {
  activeUser: {
    organizationId?: string;
    organizationRoleId?: string;
  };
  organizationOwner?: unknown;
  organizationRoles?: unknown;
}

/**
 * Who the Preferences page belongs to and whether the signed-in user may edit it. Shared by the page
 * and by the PUSH Daily Dashboard defaults so the rule lives in one place.
 *
 * - `roleName`: the organization role, or `"Owner"` for a user outside an organization.
 * - `notAbleToEdit`: an organization member that is neither Owner nor Admin.
 *   Uses `isAdminOrOwner`, which does not include Team Coach.
 * - `accountOfTheTab`: the account whose preferences are shown (the user, or the organization
 *   owner for an Admin).
 */
export const getPreferencesAccess = <
  TActiveUser extends PreferencesAccessLocals["activeUser"],
  TOwner,
>({
  activeUser,
  organizationOwner,
  organizationRoles,
}: {
  activeUser: TActiveUser;
  organizationOwner?: TOwner;
  organizationRoles?: unknown;
}) => {
  const roleName: string = activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles as never, activeUser.organizationRoleId)
    : "Owner";
  const notAbleToEdit =
    !!activeUser.organizationId &&
    !!organizationOwner &&
    !isAdminOrOwner(roleName);
  const accountOfTheTab = roleName === "Owner" ? activeUser : organizationOwner;

  return { roleName, notAbleToEdit, accountOfTheTab };
};
